import { cn } from "cn";
import fileDownload from "js-file-download";
import { AnimatePresence, motion } from "motion/react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { BwChevronLeftLine, BwChevronRightLine } from "@/components/ui/icons";
import LightBoxContent from "@/components/ui/lightbox/light-box-content";
import LightBoxHeader from "@/components/ui/lightbox/light-box-header";
import LightBoxNavigationButton from "@/components/ui/lightbox/light-box-navigation-button";
import { Slider } from "@/components/ui/slider";
import "@/components/ui/lightbox/light-box.css";
import type { LightBoxAttachment } from "@/components/ui/lightbox/light-box-attachment";
import { resolveDownloadUrl } from "@/components/ui/lightbox/utils";
import { Overlay } from "@/components/ui/overlay";
import { env } from "@/env";

/**
 * react-slick renders its OWN default arrow when `prevArrow`/`nextArrow` is
 * null or undefined, so an empty fragment is the only way to hide the arrows on
 * a single-slide lightbox — this fragment is load-bearing, not redundant.
 */
const HIDDEN_ARROW = <></>;

export async function download(url: string, filename: string) {
  const response = await fetch(url);
  const blob = await response.blob();
  fileDownload(blob, filename);
}
export type LightBoxProps<
  TAttachment extends LightBoxAttachment = LightBoxAttachment,
> = {
  open: boolean;
  slideIndex?: number;
  attachments: TAttachment[];
  onClose: () => void;
  onProgress?: (state: any) => void;
  download?: boolean;
  dataTest?: string;
  /** What the open slide shows, under it: a photo's label, where it was taken, and so on. */
  caption?: (attachment: TAttachment) => ReactNode;
};

const LightBox = <TAttachment extends LightBoxAttachment>(
  props: LightBoxProps<TAttachment>,
) => {
  const {
    open,
    slideIndex,
    attachments,
    onClose,
    onProgress,
    download = true,
    caption,
  } = props;
  const ref = useRef<HTMLInputElement>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  useEffect(() => {
    setCurrentSlideIndex(slideIndex || 0);
  }, [slideIndex]);

  const handleAfterChangeSlide = (index: number) => {
    setCurrentSlideIndex(index);
  };

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (
        ref.current &&
        (event.target.className === "slide" ||
          event.target.className === "slick-slide slick-active" ||
          event.target.className === "slick-slide slick-active slick-current")
      ) {
        onClose();
      }
    }

    function handleExit(event: any) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleExit);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleExit);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const openAttachment = attachments[currentSlideIndex];

  return (
    <Overlay
      open={open}
      className="bg-bg-dark-max/72! backdrop-blur"
      align="none"
      animation={false}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-test="light-box-container"
          >
            <LightBoxHeader
              slideIndex={currentSlideIndex}
              totalSlides={attachments.length}
              urlDownload={
                download && attachments?.[currentSlideIndex]
                  ? resolveDownloadUrl(attachments[currentSlideIndex], {
                      mediaServiceUrl: env.PUBLIC_MEDIA_SERVICE_URL,
                    })
                  : ""
              }
              onClose={onClose}
              dataTest={`${props["dataTest"]}-header`}
            />
            <div ref={ref} className="flex items-center h-screen">
              <Slider
                className="light-box__slider"
                initialSlide={slideIndex}
                dots={false}
                {...(attachments?.length > 1 ? { lazyLoad: "ondemand" } : {})}
                arrows
                prevArrow={
                  attachments?.length === 1 ? (
                    HIDDEN_ARROW
                  ) : (
                    <LightBoxNavigationButton>
                      <BwChevronLeftLine
                        data-test="light-box-prev-button"
                        size={20}
                        className="text-text-light cursor-pointer"
                      />
                    </LightBoxNavigationButton>
                  )
                }
                nextArrow={
                  attachments?.length === 1 ? (
                    HIDDEN_ARROW
                  ) : (
                    <LightBoxNavigationButton>
                      <BwChevronRightLine
                        data-test="light-box-next-button"
                        size={20}
                        className="text-text-light cursor-pointer"
                      />
                    </LightBoxNavigationButton>
                  )
                }
                afterChange={handleAfterChangeSlide}
              >
                {attachments.map((attachment, index) => (
                  <div key={attachment.id} onClick={onClose}>
                    <div
                      className="mx-auto"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <LightBoxContent
                        attachment={attachment}
                        open={currentSlideIndex === index}
                        onProgress={onProgress}
                      />
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
            {caption && openAttachment && (
              // Bottom left, clear of the zoom buttons at the bottom centre.
              <div
                className={cn(
                  /* sorted */
                  "absolute bottom-5 left-6 z-10 overflow-y-auto",
                  "max-h-[40vh] max-w-sm",
                  "px-4 py-3",
                  "text-sm text-text-dark-max",
                  "bg-bg-light rounded-xl shadow-lg",
                )}
                data-test="light-box-caption"
              >
                {caption(openAttachment)}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Overlay>
  );
};

export default LightBox;
